import type { Freshness, TodayResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { AttentionItem } from '@/components/pulse/attention-item';
import { DayControl, NotRecorded, useBusinessDate } from '@/components/pulse/day';
import { FigureValue, IncompleteMark } from '@/components/pulse/freshness';
import { AlertIcon, ArrowIcon } from '@/components/pulse/icons';
import { Kpis } from '@/components/pulse/kpis';
import { PageHeader } from '@/components/pulse/page-header';
import { PersonCard } from '@/components/pulse/person-card';
import { SourceLink } from '@/components/pulse/source-link';
import { useShell } from '@/domains/shell/api';
import { useToday } from '@/domains/today/api';

/** Today (TOD-1 to TOD-8), built from the prototype's `viewToday()`. */
export function TodayPage(): React.ReactElement {
  const { date, today } = useBusinessDate();
  const page = useToday(date, today);
  const shell = useShell();
  return (
    <>
      <PageHeader
        eyebrow="Team day · Eastern time"
        title={page.data?.title ?? ''}
        sub="From GitHub (ten24group), Teamwork and Talos's run log. Refreshed hourly by Talos."
        right={<DayControl date={date} today={today} />}
      />
      {page.data !== undefined ? <TodayView data={page.data} freshness={shell.data?.freshness ?? null} /> : null}
      {page.isError ? <span className="sub">Today couldn&apos;t be loaded. Try again in a moment.</span> : null}
    </>
  );
}

/** Everything under the header, from the page's read model. */
export function TodayView({ data, freshness }: { readonly data: TodayResponse; readonly freshness: Freshness | null }): React.ReactElement {
  const { tiles, attention, shipped } = data;
  const productionCount = shipped.count;
  return (
    <>
      <Kpis
        items={[
          {
            label: 'Merged',
            value: tiles.merged.count,
            sub: (
              <>
                {tiles.merged.breakdown || 'Nothing merged this day'} <IncompleteMark figure="merged" freshness={freshness} />
              </>
            ),
          },
          { label: 'Shipped to production', value: tiles.shippedToProduction.count, sub: tiles.shippedToProduction.sub, href: '/shipped' },
          {
            label: 'Waiting on you',
            value: <FigureValue value={tiles.waitingOnYou.count} unavailableReason="You aren't on the roster yet" />,
            sub: 'Reviews, decisions and your blocked PRs',
            warn: (tiles.waitingOnYou.count ?? 0) > 0,
            href: '/inbox',
          },
          {
            label: 'Talos work items',
            value: <FigureValue value={tiles.talosWorkItems.count} unavailableReason={tiles.talosWorkItems.unavailableReason ?? ''} />,
            sub: tiles.talosWorkItems.sub,
          },
        ]}
      />
      {/* By project sits above People (Sumit, 2026-09-27): the products that moved come first. */}
      <section className="stack" style={{ gap: 14 }} aria-labelledby="bph">
        <h2 id="bph">By project</h2>
        {data.projects.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>No project moved this day.</span> : null}
        <div className="projects">
          {data.projects.map((project) => (
            <Link key={project.productName} className="card pj" to={project.href}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>{project.productName}</span>
              <span style={{ color: 'var(--ink-2)' }}>{project.line}</span>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>{project.people}</span>
            </Link>
          ))}
        </div>
      </section>
      {/* Each column flows on its own, so What shipped follows People however tall Needs attention is. */}
      <div className="two">
        <div className="stack" style={{ gap: 28 }}>
          <section className="stack" style={{ gap: 14 }} aria-labelledby="ph">
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h2 id="ph">People</h2>
              <Link className="linkish" to="/people">
                Everyone <ArrowIcon />
              </Link>
            </div>
            <div className="people">
              {data.people.map((person) => (
                <PersonCard key={person.userId} person={person} />
              ))}
            </div>
          </section>
          <section className="stack" style={{ gap: 14 }} aria-labelledby="shh">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <h2 id="shh">What shipped</h2>
            <Link className="linkish" to="/shipped">
              The whole week <ArrowIcon />
            </Link>
          </div>
          <div className="card pad stack" style={{ gap: 4 }}>
            <span className="eyebrow" style={{ paddingBottom: 6 }}>
              To production on {shipped.weekday} · {productionCount} {productionCount === 1 ? 'PR' : 'PRs'}
            </span>
            {shipped.live.map((line) => (
              <SourceLink key={line.ref} className="prow" href={line.url} style={{ textDecoration: 'none', color: 'var(--ink)', gridTemplateColumns: 'minmax(0,1fr) auto' }}>
                <span className="stack" style={{ gap: 2 }}>
                  <span>{line.title}</span>
                  <span className="mono" style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {line.ref}
                    {line.sub !== null ? ` · ${line.sub}` : ''}
                  </span>
                </span>
                <span className="chip c-merged">Live</span>
              </SourceLink>
            ))}
            {shipped.live.length === 0 && shipped.housekeepingCount === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nothing reached production this day.</span> : null}
            {shipped.housekeepingCount > 0 || shipped.laterDays.length > 0 ? (
              <span style={{ fontSize: 13, color: 'var(--muted)', paddingTop: 8 }}>
                {shipped.housekeepingCount > 0 ? `Plus ${shipped.housekeepingCount} housekeeping ${shipped.housekeepingCount === 1 ? 'PR' : 'PRs'}.` : ''}
                {shipped.laterDays.length > 0 ? ` ${shipped.laterDays.join('. ')}.` : ''}
              </span>
            ) : null}
          </div>
          </section>
        </div>
        <div className="stack" style={{ gap: 28 }}>
          <aside className="card pad stack" style={{ gap: 20 }} aria-labelledby="ah">
            <div className="row" style={{ gap: 10, color: 'var(--warn-ink)' }}>
              <AlertIcon />
              <h2 id="ah" style={{ color: 'var(--ink)' }}>
                Needs attention
              </h2>
              <IncompleteMark figure="waiting" freshness={freshness} />
            </div>
            {!attention.recorded ? (
              <NotRecorded />
            ) : (
              <>
                {attention.groups
                  .filter((group) => group.rows.length > 0)
                  .map((group) => (
                    <div key={group.group} className="stack" style={{ gap: 10 }}>
                      <div className="row" style={{ justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 600 }}>{group.group}</span>
                        <span className={`chip ${group.tone}`}>{group.rows.length}</span>
                      </div>
                      {group.rows.map((row) => (
                        <AttentionItem key={row.ref} row={row} />
                      ))}
                    </div>
                  ))}
                <div className="stack" style={{ gap: 10, paddingTop: 16, borderTop: '1px solid var(--line-2)' }}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 600 }}>Risk flags</span>
                    <span className="chip c-changes">{attention.riskFlags.length}</span>
                  </div>
                  {attention.riskFlags.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>No risks flagged.</span> : null}
                  {attention.riskFlags.map((flag) => (
                    <AttentionItem key={flag.statement} row={{ ref: '', url: flag.link, title: flag.statement, sub: flag.detail }} />
                  ))}
                </div>
                <div className="stack" style={{ gap: 6, paddingTop: 16, borderTop: '1px solid var(--line-2)' }}>
                  <span style={{ fontWeight: 600 }}>Stuck work</span>
                  {attention.stuck.length === 0 ? (
                    <span style={{ color: 'var(--ink-2)' }}>Nothing active went quiet this week.</span>
                  ) : (
                    attention.stuck.map((row) => <AttentionItem key={row.ref} row={row} />)
                  )}
                  {attention.idleLine !== null ? <span style={{ fontSize: 13, color: 'var(--muted)' }}>{attention.idleLine}</span> : null}
                </div>
              </>
            )}
          </aside>
        <div className="card pad stack" style={{ gap: 12 }}>
          <span className="eyebrow">To dev this week</span>
          {shipped.toDev.map((product) => (
            <div key={product.productName} className="stack" style={{ gap: 2 }}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 600 }}>{product.productName}</span>
                <b className="num">{product.count}</b>
              </div>
              <span style={{ fontSize: 12, color: product.warn ? 'var(--warn-ink)' : 'var(--muted)' }}>{product.production}</span>
            </div>
          ))}
          {shipped.toDev.length === 0 ? <span style={{ color: 'var(--ink-2)' }}>Nothing merged to a dev branch this week.</span> : null}
        </div>
        </div>
      </div>
    </>
  );
}
